import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { posSetup, getApiError } from '../api/client';
import { usePos } from '../contexts/PosContext';
import { useAuth } from '../contexts/AuthContext';
import { colors, radii } from '../theme';

/**
 * Says out loud what the server would otherwise only say as a 402 on the first sale of the day:
 * this till has no valid fiscalization licence.
 *
 * It warns, it does not lock. Reading and exporting the coupons already issued is never blocked —
 * a business that stops paying must still be able to produce its records for ATK. Only issuing new
 * ones is refused, and that refusal is the server's (RequiresFiscalizationAddon), not this
 * component's; the till merely repeats the subscription it last pulled.
 */
export default function PosLicenseBanner() {
  const { isPos, isActivated, licenseValid, status } = usePos();
  // Forcing a sync is an Owner/Manager action server-side; a cashier sees the reason, not a button
  // that would only come back 403.
  const { canManage } = useAuth();
  const qc = useQueryClient();

  const sync = useMutation({
    mutationFn: () => posSetup.syncNow(),
    onSuccess: (r) => {
      qc.invalidateQueries(['pos-setup-status']);
      if (r.data.success) toast.success('Gjendja e abonimit u rifreskua nga zyra qendrore.');
      else toast.error(r.data.message);
    },
    onError: (e) => toast.error(getApiError(e, 'Sinkronizimi dështoi.')),
  });

  if (!isPos || !isActivated || licenseValid) return null;

  const noAddon = status?.subscriptionStatus === 'Active' && !status?.hasFiscalizationAddon;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 18px',
      background: colors.dangerSoft, borderBottom: `1px solid ${colors.danger}`,
      color: colors.danger,
    }}>
      <ShieldAlert size={19} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.65 }}>
        <strong>Arka nuk mund të lëshojë kuponë fiskalë.</strong>{' '}
        {noAddon
          ? 'Abonimi është aktiv, por shtesa e fiskalizimit (+10€/muaj) nuk është e aktivizuar.'
          : 'Abonimi i kompanisë nuk është aktiv (pagesa nuk është kryer).'}
        <div style={{ color: colors.textMuted, marginTop: 3 }}>
          Aktivizoje në zyrën qendrore te <strong>Pagesa &amp; Abonimi → Fiskalizimi (SEF)</strong>,
          pastaj rifresko këtu. Kuponët e lëshuar më parë mbeten të lexueshëm dhe eksportohen normalisht.
        </div>
      </div>
      {canManage && (
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            background: '#fff', border: `1px solid ${colors.danger}`, borderRadius: radii.sm,
            padding: '7px 12px', fontSize: 12.5, fontWeight: 600, color: colors.danger,
            cursor: sync.isPending ? 'default' : 'pointer',
          }}
        >
          <RefreshCw size={13} /> Rifresko
        </button>
      )}
    </div>
  );
}
